import * as React from 'react';

/**
 * One user's history with one feature's widget. In localStorage because the
 * widget remounts on every page of the feature; keyed by user for shared
 * browsers.
 */
export interface IWidgetHistory {
    /**
     * Time spent with the feature open in a visible tab, added up over all its
     * pages and visits. No longer counted once it reaches ACTIVE_MS_FOR_PROMPT.
     */
    activeMs?: number;
    /**
     * Set when the user closes the nudge or sends feedback; the nudge then never
     * comes back.
     */
    promptDismissed?: boolean;
}

/** Time in the feature after which the user is asked for feedback. */
export const ACTIVE_MS_FOR_PROMPT = 2 * 60 * 1000;

/** Whether the user has used the feature long enough to have an opinion. */
export const hasUsedFeature = (history: IWidgetHistory) =>
    (history.activeMs ?? 0) >= ACTIVE_MS_FOR_PROMPT;

const readHistory = (key: string): IWidgetHistory => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) ?? '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        // Unreadable (private mode, or a value someone edited): start over.
        return {};
    }
};

const writeHistory = (key: string, history: IWidgetHistory) => {
    try {
        localStorage.setItem(key, JSON.stringify(history));
    } catch {
        // Storage full or blocked: the widget still works, it just forgets.
    }
};

const isTabVisible = () => document.visibilityState !== 'hidden';

/**
 * Adds up visible time in the feature until ACTIVE_MS_FOR_PROMPT, without
 * polling: on mount, visibility changes and unmount, plus one timeout for when
 * the time would run out. Stops for good once reached.
 *
 * Reads and writes storage directly, so the last stretch can be saved on
 * unmount and two tabs add to the same total.
 */
export const useWidgetHistory = (key: string) => {
    const [history, setHistory] = React.useState<IWidgetHistory>(() =>
        readHistory(key),
    );

    React.useEffect(() => {
        if (hasUsedFeature(readHistory(key))) return undefined;

        // Start of the stretch of visible time not yet added up, or null while
        // the tab is hidden or once tracking has stopped.
        let visibleSince: number | null = null;
        let promptTimeout: ReturnType<typeof setTimeout> | undefined;
        const listening = new AbortController();

        const stop = () => {
            clearTimeout(promptTimeout);
            listening.abort();
            visibleSince = null;
        };

        const addUp = (leaving: boolean) => {
            const now = Date.now();
            let next = readHistory(key);
            if (visibleSince !== null) {
                next = {
                    ...next,
                    activeMs: (next.activeMs ?? 0) + now - visibleSince,
                };
                writeHistory(key, next);
            }
            visibleSince = !leaving && isTabVisible() ? now : null;
            clearTimeout(promptTimeout);

            if (leaving) return;
            if (hasUsedFeature(next)) {
                stop();
                setHistory(next);
                return;
            }
            if (visibleSince !== null) {
                promptTimeout = setTimeout(
                    () => addUp(false),
                    ACTIVE_MS_FOR_PROMPT - (next.activeMs ?? 0),
                );
            }
        };

        document.addEventListener('visibilitychange', () => addUp(false), {
            signal: listening.signal,
        });
        addUp(false);

        return () => {
            // Saves the last stretch; a no-op if tracking already stopped.
            addUp(true);
            stop();
        };
    }, [key]);

    const dismissPrompt = React.useCallback(() => {
        const next = { ...readHistory(key), promptDismissed: true };
        writeHistory(key, next);
        setHistory(next);
    }, [key]);

    return { history, dismissPrompt };
};
